/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";

import { enrichWithMockedData } from "./enrichWithMockedData";
import prepareMultiPlanList from "./prepareMultiPlanList";

import axios from "connection";

import Offer, { BannerContent, Status } from "types/Offer";
import OffersSlice, { offersMultiPlanListType } from "types/OffersSlice";
import { setLoader, setModal } from "state/config-slice";
import { setDebugTransactions } from "utils/debugHelper";

import { API_URL, PPE_PATH } from "config";
import getFeature from "utils/getFeature";
import getOpco from "utils/getOpco";
import getDeeplinkToken from "utils/getDeeplinkToken";
import { selectContractAPI } from "state/user-slice";
import getMagiclinkToken from "utils/getMagiclinkToken";

const initialState: OffersSlice = {
  needsRefetching: false,
  offersList: [],
  offersSwitchableList: [],
  offersLoaded: false,
  offersMultiPlanList: {},
};

export const queryOfferWithParams = createAsyncThunk(
  "offers/queryOfferWithParams",
  async (data: Record<string, string | boolean>, { dispatch }: any) => {
    const { queryParam, partner } = data;

    try {
      let url = `${API_URL}${PPE_PATH}/offers?${queryParam}=true&partner=${partner}`;
      const deeplink = getDeeplinkToken();
      const magiclink = getMagiclinkToken();
      if (deeplink) {
        url += `&deeplinkToken=${deeplink}`;
      } else if (magiclink) {
        url += `&magiclinkToken=${magiclink}`;
      }

      const result = await axios.get(url);

      setDebugTransactions(result);

      return result.data;
    } catch (error) {
      dispatch(setLoader({ showLoader: false }));
      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
        }),
      );

      return Promise.reject(error);
    }
  },
);

export const accountRecovery = createAsyncThunk("offers/accountRecovery", async (offer: Offer, { dispatch }: any) => {
  try {
    const result = await axios.post(`${API_URL}${PPE_PATH}/netflix/accountrecovery`, {
      opco: getOpco(),
      partner: offer?.partner,
    });

    setDebugTransactions(result);
    return { success: true, status: result.status, data: result.data };
  } catch (error) {
    dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
    dispatch(
      setModal({
        showModal: true,
        errorModal: true,
      }),
    );

    return Promise.reject(error);
  }
});

export const cancelSubscription = createAsyncThunk(
  "offers/cancelSubscription",
  async (offer: Offer, { dispatch }: any) => {
    const data = { subscription: offer?.name, partner: offer?.partner, subscriptionId: offer?.subscriptionId };

    try {
      const result = await axios.put(`${API_URL}${PPE_PATH}/offers/cancel`, data);

      dispatch(setNeedsRefetching(true));
      setDebugTransactions(result);
      return { success: true, status: result.status, data: result.data };
    } catch (error) {
      dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
        }),
      );

      return Promise.reject(error);
    }
  },
);

export const revokeSubscriptionCancellation = createAsyncThunk(
  "offers/revokeSubscriptionCancellation",
  async (offer: Offer, { dispatch }: any) => {
    try {
      const result = await axios.put(`${API_URL}${PPE_PATH}/offers/revokeCancel`, {
        subscription: offer?.name,
        partner: offer?.partner,
        subscriptionId: offer?.subscriptionId,
      });

      dispatch(setNeedsRefetching(true));
      setDebugTransactions(result);
      return { success: true, status: result.status, response: result.data };
    } catch (error) {
      dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
        }),
      );

      return Promise.reject(error);
    }
  },
);

export const downgradeSubscription = createAsyncThunk(
  "offers/downgradeSubscription",
  async (offer: Offer, { dispatch }: any) => {
    try {
      const result = await axios.put(`${API_URL}${PPE_PATH}/offers/downgrade`, {
        offerName: offer?.name,
        partner: offer?.partner,
        type: offer.type,
        packageId: offer?.longPackageId,
        shortPackageId: offer?.packageId,
      });

      dispatch(setNeedsRefetching(true));
      setDebugTransactions(result);
      return { success: true, status: result.status, response: result.data };
    } catch (error) {
      dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
        }),
      );

      return Promise.reject(error);
    }
  },
);

export const activateSubscriptions = createAsyncThunk("/offers/activate", async (offer: Offer, { dispatch }: any) => {
  try {
    const result = await axios.put(`${API_URL}${PPE_PATH}/offers/activate`, {
      offerName: offer.name,
      type: offer.type,
      packageId: offer?.longPackageId,
      shortPackageId: offer?.packageId,
      partner: offer?.partner,
      subscriptionId: offer?.subscriptionId,
      switchable: offer?.switchable,
      alreadyStandalone: offer?.alreadyStandalone,
    });

    setDebugTransactions(result);
    return result.data;
  } catch (error: any) {
    try {
      const match = error.response.data.detail.match(/\{.*\}/);

      if (match) {
        const errorDetails = JSON.parse(match[0]);
        if (errorDetails.errorCode === "ER_PAYMENT_FAILED" && errorDetails.httpStatus === 500) {
          dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
          dispatch(
            setModal({
              showModal: true,
              errorModal: true,
              errorCode: errorDetails.errorCode,
              title: errorDetails.errorCode,
              description: errorDetails.errorMessage,
            }),
          );
        } else {
          dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
          dispatch(
            setModal({
              showModal: true,
              errorModal: true,
            }),
          );
        }
      }
    } catch (parseError) {
      dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
        }),
      );
    }

    return Promise.reject(error);
  }
});

export const revokeDowngrade = createAsyncThunk("/offers/revokeDowngrade", async (offer: Offer, { dispatch }: any) => {
  try {
    const result = await axios.put(`${API_URL}${PPE_PATH}/offers/revoke/downgrade`, {
      partner: offer?.partner,
      subscriptionId: offer?.subscriptionId,
    });

    setDebugTransactions(result);
    return { success: true };
  } catch (error) {
    dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
    dispatch(
      setModal({
        showModal: true,
        errorModal: true,
      }),
    );

    return Promise.reject(error);
  }
});

export const initActivation = createAsyncThunk(
  "/offers/initActivation",
  async (payload: any, { rejectWithValue }: any) => {
    try {
      const result = await axios.put(`${API_URL}${PPE_PATH}/offers/activate`, {
        offerName: payload.offer.name,
        type: payload.offer.type,
        packageId: payload.offer?.longPackageId,
        shortPackageId: payload.offer?.packageId,
        partner: payload.offer?.partner,
        subscriptionId: payload.offer?.subscriptionId,
        code: payload.code,
      });

      setDebugTransactions(result);
      return Promise.resolve(result?.data);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const activateHardbundleOffer = createAsyncThunk(
  "/offers/hardbundled/activate",
  async (data: Record<string, string | boolean | undefined>, { dispatch }: any) => {
    const {
      offerName,
      partner,
      type,
      subscriptionId,
      longPackageId,
      packageId,
      firstName,
      lastName,
      email,
      password,
      alreadyStandalone,
      switchable,
    } = data;

    try {
      const result = await axios.put(`${API_URL}${PPE_PATH}/offers/activate`, {
        offerName,
        type,
        packageId: longPackageId,
        shortPackageId: packageId,
        partner,
        subscriptionId,
        alreadyStandalone,
        switchable,
        userData: {
          firstName,
          lastName,
          email,
          password,
        },
      });

      setDebugTransactions(result);
      return result.data;
    } catch (error) {
      dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
        }),
      );

      return Promise.reject(error);
    }
  },
);

export const offersSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    setOffers: (state, action) => {
      const { data, cdnUrl } = action.payload;
      const enrichedData = enrichWithMockedData(data, { cdnUrl });
      const offersFromPurchasedArray = enrichedData.purchased.map((offer: Offer) => ({
        ...offer,
        fromPurchasedArray: true,
      }));

      let offersList = enrichedData
        ? [...enrichedData.available, ...offersFromPurchasedArray]
        : initialState.offersList;

      let offersMultiPlanList: offersMultiPlanListType = prepareMultiPlanList(offersList);

      offersList = offersList.filter((offer: Offer) => {
        const featureEnabled = getFeature("Multi Plan List");
        const partnerPlans = offersMultiPlanList[offer.partner];
        const isNotHardbundle = offer.type !== "hardbundle";
        const isMainOffer = offer.name === partnerPlans?.mainOffer.name;

        if (partnerPlans && isNotHardbundle) {
          return featureEnabled && isMainOffer;
        }

        return true;
      });

      return {
        ...state,
        offersList,
        offersMultiPlanList,
      };
    },
    setSwitchableOffers: (state, action) => {
      const { data, cdnUrl } = action.payload;
      const enrichedData = enrichWithMockedData(data, { cdnUrl });

      return {
        ...state,
        offersSwitchableList: enrichedData ? [...enrichedData.available] : initialState.offersSwitchableList,
      };
    },
    clearOffersState: () => {
      return {
        ...initialState,
      };
    },
    setNeedsRefetching: (state, action) => {
      return {
        ...state,
        needsRefetching: action.payload || false,
      };
    },
    setOffersLoaded: (state, action) => {
      return {
        ...state,
        offersLoaded: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(selectContractAPI.fulfilled, (state) => {
      state.needsRefetching = true;
    });
  },
});

export const { setOffers, clearOffersState, setNeedsRefetching, setSwitchableOffers, setOffersLoaded } =
  offersSlice.actions;

export const availableOffers = createSelector(
  (state) => state?.offers?.offersList || [],
  (offersList) => offersList as Offer[],
);

export const multiPlanOffers = createSelector(
  (state) => state?.offers?.offersMultiPlanList,
  (offersMultiPlanList) => {
    let allMultiPlanOffers: Offer[] = [];

    for (const key in offersMultiPlanList as offersMultiPlanListType) {
      const extraPlans = (offersMultiPlanList as offersMultiPlanListType)[key].extraPlanOffers || [];
      allMultiPlanOffers = [...allMultiPlanOffers, ...extraPlans];
    }

    return allMultiPlanOffers;
  },
);

export const purchasedOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status !== Status.purchasable) || [],
);

export const purchasedStandaloneOffers = createSelector(
  availableOffers,
  (offersList) =>
    offersList.filter(
      (el) =>
        (el?.type === "standalone" && el?.marketplaceInfo?.status !== Status.purchasable) ||
        (el?.marketplaceInfo?.status === Status.cancelled &&
          Date.now() <= Date.parse(el?.endDate as string) &&
          el?.type === "standalone"),
    ) || [],
);

export const purchasableOffers = createSelector(
  availableOffers,
  (offersList) =>
    offersList.filter(
      (el: { marketplaceInfo: { status: Status } }) => el?.marketplaceInfo?.status === Status.purchasable,
    ) || [],
);

export const offersWithBannerSlides = createSelector(availableOffers, (offersList): BannerContent[] => {
  const filteredOffers = offersList.filter(
    (el) =>
      (el?.marketplaceInfo?.banner?.carousel && el?.marketplaceInfo?.status === Status.purchasable) ||
      el?.marketplaceInfo?.banner?.timeBoundPromo?.carousel,
  );

  const selectedFields: BannerContent[] = filteredOffers.flatMap<BannerContent>((offer) => {
    const banner = offer?.marketplaceInfo?.banner;
    const promo = banner?.timeBoundPromo;

    const results: BannerContent[] = [];

    if (banner?.carousel) {
      results.push({
        carousel: true,
        name: offer.name ?? "",
        partner: offer.partner ?? "",
        images: offer.images,
        order: banner.order ?? 0,
        isVideo: banner.isVideo ?? false,
        description: banner.description ?? "",
        title: banner.title ?? "",
        action: banner.action ?? { type: "", label: "" },
        sourceType: "banner",
      } satisfies BannerContent);
    }

    if (promo?.carousel) {
      results.push({
        carousel: true,
        name: offer.name ?? "",
        partner: offer.partner ?? "",
        images: offer.images,
        order: promo.order ?? 0,
        isVideo: promo.isVideo ?? false,
        description: promo.description ?? "",
        title: promo.title ?? "",
        action: promo.action ?? { type: "", label: "" },
        sourceType: "timeBoundPromo",
      } satisfies BannerContent);
    }

    return results;
  });

  const defaultElement: BannerContent = {
    carousel: true,
    name: "",
    partner: "default",
    images: undefined,
    isVideo: false,
    order: 0,
    title: "",
    description: "",
    action: { type: "", label: "" },
    sourceType: "banner",
  };

  selectedFields.unshift(defaultElement);

  selectedFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return selectedFields;
});

export const purchasableExternallyOffers = createSelector(
  availableOffers,
  (offersList) =>
    offersList.filter(
      (el: { marketplaceInfo: { status: Status } }) => el?.marketplaceInfo?.status === Status.purchasableExternally,
    ) || [],
);

export const pendingActivationOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status === Status.pendingActivation) || [],
);

export const frozenOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status === Status.frozen) || [],
);

export const activeOffers = createSelector(
  availableOffers,
  (offersList) =>
    offersList.filter(
      (el) =>
        el?.marketplaceInfo?.status === Status.active ||
        el?.marketplaceInfo?.status === Status.optInRequested ||
        el?.marketplaceInfo?.status === Status.optInDeclined,
    ) || [],
);

export const notPurchasableOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status === Status.notPurchasable) || [],
);

export const cancelledOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status === Status.cancelled) || [],
);

export const provisioningOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status === Status.provisioning) || [],
);

export const cardExpiredOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status === Status.cardExpired) || [],
);

export const cardPendingOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status === Status.cardPending) || [],
);

export const hardbundleActiveOffers = createSelector(
  availableOffers,
  (offersList) =>
    offersList.filter((el) => el?.marketplaceInfo?.status !== Status.purchasable && el.type === "hardbundle") || [],
);

export const maintainanceOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status === Status.maintainance) || [],
);

export const OptinOffers = createSelector(
  availableOffers,
  (offersList) => offersList.filter((el) => el?.marketplaceInfo?.status === Status.optInRequested) || [],
);

export const standaloneActiveOffers = createSelector(
  availableOffers,
  (offersList) =>
    offersList.filter(
      (el) =>
        (el?.marketplaceInfo?.status === Status.active && el.type === "standalone") ||
        (el?.marketplaceInfo?.status === Status.cancelled &&
          Date.now() <= Date.parse(el?.endDate as string) &&
          el?.type === "standalone"),
    ) || [],
);

export const offersState = createSelector(
  (state) => state.offers,
  (offers) => offers as OffersSlice,
);

export const selectOfferByName = (name: string | undefined): any =>
  createSelector(
    availableOffers,
    switchableOffers,
    multiPlanOffers,
    (offersList, offersSwitchableList, offersMultiPlanList) =>
      offersList?.filter((el) => el.name && el.name === name)?.[0] ||
      offersSwitchableList?.filter((el) => el.name && el.name === name)?.[0] ||
      offersMultiPlanList?.filter((el) => el.name && el.name === name)?.[0] ||
      null,
  );

export const selectOfferByDeepLink = (deepLink: string | undefined): any =>
  createSelector(
    availableOffers,
    switchableOffers,
    multiPlanOffers,
    (offersList, offersSwitchableList, offersMultiPlanList) => {
      const allOffers = [...offersList, ...offersSwitchableList, ...offersMultiPlanList];

      return allOffers.find((el) => el.marketplaceInfo.deepLink === deepLink) || null;
    },
  );

export const selectOfferByPartner = (partner: string | undefined | null): any =>
  createSelector(
    availableOffers,
    (offersList) => offersList?.filter((el) => el.partner && el.partner === partner)?.[0] || null,
  );

export const selectOffersByPartner = (partner: string | undefined | null): any =>
  createSelector(
    availableOffers,
    (offersList) => offersList?.filter((el) => el.partner && el.partner === partner) || null,
  );

export const availablePartners = createSelector(availableOffers, (offersList) => [
  ...new Set(offersList?.map((el) => el.partner)),
]);

export const switchableOffers = createSelector(
  (state) => state?.offers?.offersSwitchableList || [],
  (offersSwitchableList) => offersSwitchableList as Offer[],
);

export const selectSwitchableOffersByPartner = (partner: string | undefined | null): any =>
  createSelector(
    switchableOffers,
    (offersSwitchableList) => offersSwitchableList?.filter((el) => el.partner && el.partner === partner) || null,
  );

export const selectSwitchableOfferByName = (name: string | undefined): any =>
  createSelector(
    switchableOffers,
    (offersSwitchableList) => offersSwitchableList?.filter((el) => el.name && el.name === name)?.[0] || null,
  );

export default offersSlice.reducer;
